import React, { useState } from 'react';
import { MdUpload, MdSave, MdInfo } from 'react-icons/md';
import { guardarDevolucionesManuales } from '../api/apiConfig';

const IngresarDevolucionesManuales = () => {
  const [csvData, setCsvData] = useState([]);
  const [summary, setSummary] = useState({ registros: 0, totalCargos: 0, totalAbonos: 0 });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setError('Por favor seleccione un archivo CSV válido');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          setError('El archivo CSV debe contener al menos una fila de datos');
          return;
        }

        const headers = lines[0].split(';').map(h => h.trim());
        const expectedHeaders = ['FECHA', 'COMPROBANTE', 'CENTRO_COSTO', 'RUT', 'DOCUMENTO', 'FECHA_DOCTO', 'GLOSA', 'CARGOS', 'ABONOS', 'ANIO_DEV', 'MES_DEV'];
        
        const hasAllHeaders = expectedHeaders.every(header => headers.includes(header));
        if (!hasAllHeaders) {
          setError('El archivo CSV debe contener las columnas: ' + expectedHeaders.join(', '));
          return;
        }

        const data = [];
        let totalCargos = 0;
        let totalAbonos = 0;

        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(';').map(v => v.trim());
          if (values.length === headers.length) {
            const row = {};
            headers.forEach((header, index) => {
              row[header] = values[index];
            });
            
            const cargos = parseFloat(row.CARGOS) || 0;
            const abonos = parseFloat(row.ABONOS) || 0;
            
            totalCargos += cargos;
            totalAbonos += abonos;
            
            data.push(row);
          }
        }

        setCsvData(data);
        setSummary({
          registros: data.length,
          totalCargos,
          totalAbonos
        });
        setError('');
      } catch (err) {
        setError('Error al procesar el archivo CSV');
        console.error('Error:', err);
      }
    };
    
    reader.readAsText(file);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(value);
  };

  const handleSaveData = async () => {
    try {
      setSaving(true);
      setError('');
      setSaveSuccess(false);
      
      const formatDateToISO = (dateStr) => {
        if (!dateStr) return dateStr;
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        }
        return dateStr;
      };
      
      const formattedData = csvData.map(row => ({
        FECHA: formatDateToISO(row.FECHA),
        COMPROBANTE: row.COMPROBANTE,
        CENTRO_COSTO: row.CENTRO_COSTO,
        RUT: row.RUT,
        DOCUMENTO: row.DOCUMENTO,
        FECHA_DOCTO: formatDateToISO(row.FECHA_DOCTO),
        GLOSA: row.GLOSA,
        CARGOS: parseFloat(row.CARGOS) || 0,
        ABONOS: parseFloat(row.ABONOS) || 0,
        ANIO_DEV: parseInt(row.ANIO_DEV) || 0,
        MES_DEV: parseInt(row.MES_DEV) || 0
      }));
      
      await guardarDevolucionesManuales(formattedData);
      setSaveSuccess(true);
    } catch (err) {
      setError('Error al guardar los datos: ' + (err.message || 'Error desconocido'));
      console.error('Error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-baseline gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Ingresar Devoluciones Manuales</h1>
      </div>

      <div className="seccion-datos">
        <h3 style={{color: 'var(--if-primary)', margin: '0 0 1rem 0', fontSize: '1.2rem', fontWeight: '600'}}>
          Cargar Archivo CSV
        </h3>
        
        <div className="mb-4 p-4" style={{backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: '8px'}}>
          <div className="flex items-start gap-3">
            <MdInfo className="text-blue-600 mt-1" size={20} />
            <div>
              <h4 className="font-semibold text-blue-800 mb-2">Formato del archivo CSV:</h4>
              <p className="text-sm text-blue-700 mb-2">
                El archivo debe contener las siguientes columnas separadas por punto y coma (;):
              </p>
              <div className="text-xs text-blue-600 font-mono bg-blue-50 p-2 rounded">
                FECHA;COMPROBANTE;CENTRO_COSTO;RUT;DOCUMENTO;FECHA_DOCTO;GLOSA;CARGOS;ABONOS;ANIO_DEV;MES_DEV
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">
            Seleccionar archivo CSV:
          </label>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>

        {error && (
          <div className="mb-4 p-3 rounded bg-red-100 text-red-700 border border-red-300">
            {error}
          </div>
        )}

        {saveSuccess && (
          <div className="mb-4 p-3 rounded bg-green-100 text-green-700 border border-green-300">
            Datos guardados exitosamente en la base de datos
          </div>
        )}

        {csvData.length > 0 && (
          <>
            <div className="flex justify-between items-center mb-4">
              <h4 style={{color: 'var(--if-primary)', fontSize: '1.1rem', fontWeight: '600'}}>
                Vista previa de datos ({csvData.length} registros)
              </h4>
              <button
                onClick={handleSaveData}
                disabled={saving}
                className="btn-primary flex items-center gap-2"
                style={{
                  background: saving ? '#9ca3af' : 'linear-gradient(135deg, var(--if-success) 0%, #16a34a 100%)',
                  cursor: saving ? 'not-allowed' : 'pointer'
                }}
              >
                <MdSave size={16} />
                {saving ? 'Guardando...' : 'Guardar en Base de Datos'}
              </button>
            </div>

            {/* Summary Cards */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="text-blue-800 font-semibold">Total Registros</div>
                <div className="text-2xl font-bold text-blue-900">{summary.registros}</div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="text-green-800 font-semibold">Total Cargos</div>
                <div className="text-2xl font-bold text-green-900">{formatCurrency(summary.totalCargos)}</div>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <div className="text-orange-800 font-semibold">Total Abonos</div>
                <div className="text-2xl font-bold text-orange-900">{formatCurrency(summary.totalAbonos)}</div>
              </div>
            </div>

            <div className="tabla-container" style={{overflowX: 'auto', maxHeight: '400px', overflowY: 'auto'}}>
              <table className="w-full" style={{fontSize: '0.875rem', minWidth: '1200px'}}>
                <thead style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1}}>
                  <tr>
                    <th style={{backgroundColor: 'var(--if-primary)', color: 'white', minWidth: '100px'}}>Fecha</th>
                    <th style={{backgroundColor: 'var(--if-primary)', color: 'white', minWidth: '100px'}}>Comprobante</th>
                    <th style={{backgroundColor: 'var(--if-primary)', color: 'white', minWidth: '100px'}}>Centro Costo</th>
                    <th style={{backgroundColor: 'var(--if-primary)', color: 'white', minWidth: '120px'}}>RUT</th>
                    <th style={{backgroundColor: 'var(--if-primary)', color: 'white', minWidth: '100px'}}>Documento</th>
                    <th style={{backgroundColor: 'var(--if-primary)', color: 'white', minWidth: '100px'}}>Fecha Docto</th>
                    <th style={{backgroundColor: 'var(--if-primary)', color: 'white', minWidth: '250px'}}>Glosa</th>
                    <th style={{backgroundColor: '#fef3c7', color: 'white', minWidth: '120px'}}>Cargos</th>
                    <th style={{backgroundColor: '#dcfce7', color: 'white', minWidth: '120px'}}>Abonos</th>
                    <th style={{backgroundColor: 'var(--if-primary)', color: 'white', minWidth: '80px'}}>Año Dev</th>
                    <th style={{backgroundColor: 'var(--if-primary)', color: 'white', minWidth: '80px'}}>Mes Dev</th>
                  </tr>
                </thead>
                <tbody>
                  {csvData.map((row, index) => (
                    <tr key={index}>
                      <td>{row.FECHA}</td>
                      <td>{row.COMPROBANTE}</td>
                      <td>{row.CENTRO_COSTO}</td>
                      <td>{row.RUT}</td>
                      <td>{row.DOCUMENTO}</td>
                      <td>{row.FECHA_DOCTO}</td>
                      <td>{row.GLOSA}</td>
                      <td style={{textAlign: 'right'}}>{formatCurrency(parseFloat(row.CARGOS) || 0)}</td>
                      <td style={{textAlign: 'right'}}>{formatCurrency(parseFloat(row.ABONOS) || 0)}</td>
                      <td style={{textAlign: 'center'}}>{row.ANIO_DEV}</td>
                      <td style={{textAlign: 'center'}}>{row.MES_DEV}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default IngresarDevolucionesManuales;